// New file: src/pages/AppraisalPage.jsx

import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  Button,
  Card,
  Label,
  TextInput,
  Select,
  Table,
  Modal,
  Dropdown,
  Badge,
} from "flowbite-react";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import {
  HiCalendar,
  HiChartBar,
  HiCheckCircle,
  HiDotsVertical,
  HiEye,
  HiPencilAlt,
  HiPlus,
  HiStar,
} from "react-icons/hi";
import CustomToast from "../components/Toast";
import { months } from "../utils/months";
import { IoMdArrowBack } from "react-icons/io";
import { FiFilter } from "react-icons/fi";
import DashboardLayout from "../components/layout/DashboardLayout";

function Appraisal() {
  const store = useContext(Store);
  const [baseUrl] = store.url;
  const token = localStorage.getItem("royal-token");

  const [kpis, setKpis] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toLocaleString("default", { month: "long" }),
  );
  const [_year, setYear] = useState();
  const [_month, setMonth] = useState();
  const [viewingAppraisal, setViewingAppraisal] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({});
  const [appraisalList, setAppraisalList] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [department, setDepartment] = useState("");
  const [isReviewMode, setIsReviewMode] = useState(false); // for admin review mode
  const [toast, setToast] = useState({ open: false, msg: "", type: "" });
  const [editingAppraisal, setEditingAppraisal] = useState(null);

  const [secondCard, setSecondCard] = useState(false);

  const years = Array.from({ length: 6 }, (_, i) => 2023 + i);

  useEffect(() => {
    const fetchRole = async () => {
      const user = await apiRequest(`${baseUrl}/token`, "GET", null, token);
      setIsAdmin(user?.data?.role?.title === "Admin");
      setDepartment(user?.data?.lastName);
    };
    fetchRole();
  }, [baseUrl, token]);

  useEffect(() => {
    const fetchKpis = async () => {
      try {
        const res = await apiRequest(
          `${baseUrl}/kpi-by-department?department=${department}`,
          "GET",
          null,
          token,
        );
        setKpis(res.data);
      } catch (err) {
        handleToast("Error loading KPIs", "error");
      }
    };

    if (department) {
      fetchKpis();
    }
  }, [baseUrl, token, department]);

  const loadAppraisals = useCallback(async () => {
    try {
      const token = localStorage.getItem("royal-token");

      const params = new URLSearchParams();
      if (_month) params.append("month", _month);
      if (_year) params.append("year", _year);

      const url = `${baseUrl}/appraisals${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const res = await apiRequest(url, "GET", null, token);

      setAppraisalList(res.data);
    } catch (error) {
      handleToast("Failed to load appraisals.", "error");
    }
  }, [baseUrl, _month, _year]);

  useEffect(() => {
    loadAppraisals();
  }, [loadAppraisals]);

  const handleToast = (msg, type) => {
    setToast({ open: true, msg, type });
    setTimeout(() => setToast({ open: false, msg: "", type: "" }), 5000);
  };

  const handleChange = (kpiId, value) => {
    setFormData((prev) => ({ ...prev, [kpiId]: Number(value) }));
  };

  const handleSubmit = async () => {
    const payload = {
      year: selectedYear,
      month: selectedMonth,
      kpis: Object.entries(formData).map(([kpiId, score]) => ({
        kpiId,
        score,
      })),
    };

    try {
      const url = `${baseUrl}/appraisal`;
      await apiRequest(url, "POST", payload, token);
      handleToast("Appraisal submitted.", "success");
      setFormData({});
    } catch (err) {
      handleToast(err.message || "Error submitting", "error");
    }
  };

  const handleScoreUpdate = async (id, isSelf = false) => {
    const url = isReviewMode
      ? `${baseUrl}/appraisal/${id}`
      : `${baseUrl}/self/appraisal/${id}`;
    const body = {
      kpis: Object.entries(formData).map(([kpiId, score]) => ({
        kpiId,
        [isSelf ? "score" : "actualPerformance"]: score,
      })),
    };
    try {
      await apiRequest(url, "PUT", body, token);
      loadAppraisals();
      handleToast("Appraisal updated.", "success");
    } catch (err) {
      handleToast(err.message || "Update failed.", "error");
    }
  };

  const handleOpenEdit = (appraisal, reviewMode = false) => {
    setEditingAppraisal(appraisal);
    setIsReviewMode(reviewMode); // make sure this is a state variable

    const initialData = {};
    appraisal.kpis.forEach((entry) => {
      const kpiId = entry.kpiId?.id;
      const value = reviewMode ? entry.actualPerformance : entry.score;

      if (kpiId) initialData[kpiId] = value;
    });

    setFormData(initialData);
  };

  const analytics = useMemo(() => {
    const totalAppraisals = appraisalList.length;
    const reviewedAppraisals = appraisalList.filter(
      (appraisal) => appraisal.overallScore !== null,
    ).length;
    const pendingAppraisals = appraisalList.filter(
      (appraisal) => appraisal.overallScore === null,
    ).length;
    const averageScore =
      reviewedAppraisals > 0
        ? Math.round(
            appraisalList.reduce(
              (sum, appraisal) => sum + Number(appraisal.overallScore || 0),
              0,
            ) / reviewedAppraisals,
          )
        : 0;

    return {
      totalAppraisals,
      reviewedAppraisals,
      pendingAppraisals,
      averageScore,
    };
  }, [appraisalList]);

  const AnalyticsCard = ({ title, value, colorClass, icon: Icon }) => (
    <Card className={`shadow-lg p-2 dark:bg-gray-800 border-l-4 ${colorClass}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            {title}
          </p>
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">
            {value}
          </h2>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </Card>
  );

  return (
    <>
      <title>Appraisal</title>
      <meta
        name="description"
        content="Royalmines Office Staff Appraisal Page"
      />
      <DashboardLayout active="appraisal" contentClassName="lg:p-8">
        <h5 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
          <HiChartBar className="w-8 h-8 mr-3 text-indigo-600" />
          Appraisal Management
        </h5>

        <hr className="my-6 border-gray-200 dark:border-gray-700" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AnalyticsCard
            title="Visible Appraisals"
            value={analytics.totalAppraisals}
            colorClass="text-indigo-500 border-indigo-500"
            icon={HiChartBar}
          />
          <AnalyticsCard
            title="Reviewed"
            value={analytics.reviewedAppraisals}
            colorClass="text-green-500 border-green-500"
            icon={HiCheckCircle}
          />
          <AnalyticsCard
            title="Pending Review"
            value={analytics.pendingAppraisals}
            colorClass="text-amber-500 border-amber-500"
            icon={HiCalendar}
          />
          <AnalyticsCard
            title="Average Score"
            value={analytics.averageScore}
            colorClass="text-cyan-500 border-cyan-500"
            icon={HiStar}
          />
        </div>

            <div>
              {!secondCard && (
                <div className="mb-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {_month || _year
                        ? `Filtered: ${_month || "All Months"} ${_year || "All Years"}`
                        : "All Appraisals"}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {!isAdmin && (
                        <Button
                          color="success"
                          onClick={() => setSecondCard(true)}
                          className="w-full sm:w-auto"
                        >
                          <HiPlus className="w-5 h-5 mr-2" />
                          New Appraisal
                        </Button>
                      )}
                      <Dropdown
                        label={
                          <Button color="light" className="w-full sm:w-auto">
                            <FiFilter className="me-2" /> Filter Month
                          </Button>
                        }
                        dismissOnClick={true}
                        inline
                      >
                        {months.map((month) => (
                          <Dropdown.Item
                            key={month}
                            className={_month === month ? "active-dropdown" : ""}
                            onClick={() => {
                              setMonth(month);
                            }}
                          >
                            {month}
                          </Dropdown.Item>
                        ))}
                      </Dropdown>
                      <Dropdown
                        label={
                          <Button color="light" className="w-full sm:w-auto">
                            <FiFilter className="me-2" /> Filter Year
                          </Button>
                        }
                        dismissOnClick={true}
                        inline
                      >
                        {years.map((year) => (
                          <Dropdown.Item
                            key={year}
                            className={_year === year ? "active-dropdown" : ""}
                            onClick={() => {
                              setYear(year);
                            }}
                          >
                            {year}
                          </Dropdown.Item>
                        ))}
                      </Dropdown>
                    </div>
                  </div>
                </div>
              )}

              {secondCard && (
                <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
                  {/* --- Go Back Button (Top Left) --- */}
                  <Button
                    outline
                    color="dark"
                    onClick={() => {
                      setSecondCard(false);
                      setFormData({});
                    }}
                    className="mb-6 w-full sm:w-auto hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    <IoMdArrowBack className="me-2 h-5 w-5" />
                    Back
                  </Button>

                  <div className="flex justify-center items-start pt-4">
                    {/* --- Main Submission Card --- */}
                    <Card className="w-full max-w-2xl shadow-2xl rounded-xl border-t-8 border-indigo-500 dark:border-indigo-400">
                      <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white flex items-center mb-2">
                        <HiChartBar className="h-8 w-8 text-indigo-500 mr-3" />
                        KPI Performance Submission
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        Input the achieved scores for the selected period.
                        Scores cannot exceed the maximum weight.
                      </p>

                      <div className="flex flex-col gap-6">
                        {/* --- Month and Year Selection (Horizontal Layout) --- */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-gray-200 dark:border-gray-700 pb-4">
                          {/* Month Select */}
                          <div>
                            <Label
                              htmlFor="month-select"
                              value="Reporting Month"
                              className="font-semibold text-gray-700 dark:text-gray-300 mb-1"
                            />
                            <Select
                              id="month-select"
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                              icon={HiCalendar}
                              className="mt-1"
                            >
                              {months.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </Select>
                          </div>

                          {/* Year Select */}
                          <div>
                            <Label
                              htmlFor="year-select"
                              value="Reporting Year"
                              className="font-semibold text-gray-700 dark:text-gray-300 mb-1"
                            />
                            <Select
                              id="year-select"
                              value={selectedYear}
                              onChange={(e) =>
                                setSelectedYear(parseInt(e.target.value))
                              }
                              icon={HiCalendar}
                              className="mt-1"
                            >
                              {years.map((y) => (
                                <option key={y} value={y}>
                                  {y}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>

                        {/* --- KPI Input Fields --- */}
                        <div className="space-y-5 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                          {kpis.map((kpi) => (
                            <div
                              key={kpi.id}
                              className="p-3 border rounded-lg dark:border-gray-700 hover:bg-indigo-50/50 dark:hover:bg-gray-700 transition-colors duration-150"
                            >
                              <div className="flex justify-between items-center mb-1">
                                <Label className="font-medium text-gray-800 dark:text-gray-200">
                                  {kpi.title}
                                </Label>
                                <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2.5 py-0.5 rounded-full dark:bg-teal-200 dark:text-teal-800">
                                  Max Score: {kpi.weight}
                                </span>
                              </div>
                              <TextInput
                                type="number"
                                step="0.01"
                                placeholder={`Enter score (max ${kpi.weight})`}
                                value={formData[kpi.id] || ""}
                                MaxScore={kpi.weight} // Note: This prop likely does nothing visually in Flowbite, keeping it for data passing if necessary
                                onChange={(e) => {
                                  // Ensure the input value is a number and does not exceed the weight
                                  const val = parseFloat(e.target.value) || 0;
                                  if (val <= kpi.weight) {
                                    handleChange(kpi.id, e.target.value);
                                  }
                                  // Optionally, add an else case to notify the user if they exceed max score
                                }}
                                min={0}
                                max={kpi.weight}
                                sizing="md"
                                color={
                                  // Add a simple visual feedback for scores
                                  formData[kpi.id] && formData[kpi.id] > 0
                                    ? "success"
                                    : "default"
                                }
                              />
                            </div>
                          ))}
                        </div>

                        {/* --- Submit Button --- */}
                        <Button
                          onClick={handleSubmit}
                          className="mt-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 font-bold"
                        >
                          Finalize & Submit KPI Scores
                        </Button>
                      </div>
                    </Card>
                  </div>
                </div>
              )}
              {!secondCard && (
                <Card className="shadow-2xl dark:bg-gray-800 overflow-x-auto min-h-[400px]">
                  {appraisalList.length === 0 ? (
                    <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
                      <HiChartBar className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
                      <h6 className="text-lg font-semibold text-gray-900 dark:text-white">
                        No appraisals found
                      </h6>
                      <p className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                        Adjust the month or year filters, or create a new
                        appraisal.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto overflow-y-auto max-h-[58vh] scrollbar-always-visible">
                      <Table hoverable striped>
                        <Table.Head className="sticky top-0 z-10">
                          <Table.HeadCell>Staff</Table.HeadCell>
                          <Table.HeadCell>Month</Table.HeadCell>
                          <Table.HeadCell>Year</Table.HeadCell>
                          <Table.HeadCell>Score</Table.HeadCell>
                          <Table.HeadCell>Status</Table.HeadCell>
                          <Table.HeadCell>
                            <span className="sr-only">Actions</span>
                          </Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y">
                          {appraisalList.map((a) => (
                            <Table.Row
                              key={a.id}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <Table.Cell className="whitespace-nowrap font-semibold text-gray-900 dark:text-white">
                                {a.staffId?.firstName} {a.staffId?.lastName}
                              </Table.Cell>
                              <Table.Cell>{a.month}</Table.Cell>
                              <Table.Cell>{a.year}</Table.Cell>
                              <Table.Cell>
                                {a.overallScore || "Not reviewed yet"}
                              </Table.Cell>
                              <Table.Cell>
                                <Badge
                                  color={
                                    a.overallScore !== null
                                      ? "success"
                                      : "warning"
                                  }
                                >
                                  {a.overallScore !== null
                                    ? "Reviewed"
                                    : "Pending"}
                                </Badge>
                              </Table.Cell>
                              <Table.Cell>
                                <Dropdown
                                  label={<HiDotsVertical className="w-5 h-5" />}
                                  inline
                                  arrowIcon={false}
                                >
                                  {!isAdmin && (
                                    <Dropdown.Item
                                      icon={HiPencilAlt}
                                      onClick={() => handleOpenEdit(a, false)}
                                      disabled={a.overallScore !== null}
                                    >
                                      Edit
                                    </Dropdown.Item>
                                  )}
                                  {isAdmin && (
                                    <Dropdown.Item
                                      icon={HiPencilAlt}
                                      onClick={() => handleOpenEdit(a, true)}
                                    >
                                      Remark
                                    </Dropdown.Item>
                                  )}
                                  <Dropdown.Item
                                    icon={HiEye}
                                    onClick={() => setViewingAppraisal(a)}
                                  >
                                    View
                                  </Dropdown.Item>
                                </Dropdown>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  )}
                </Card>
              )}
            </div>

        <Modal
          show={!!editingAppraisal}
          onClose={() => setEditingAppraisal(null)}
          size="lg"
          className="z-[10000]"
        >
          <Modal.Header>
            {isReviewMode ? "Add Appraisal Remark" : "Edit Appraisal"}{" "}
          </Modal.Header>

          <Modal.Body>
            {editingAppraisal?.kpis.map((kpiEntry) => {
              const kpiId = kpiEntry.kpiId?.id;
              const kpiTitle = kpiEntry.kpiId?.title;
              const kpiWeight = kpiEntry.kpiId?.weight;
              const kpiScore = kpiEntry.score;

              return (
                <div
                  key={kpiId}
                  className="mb-6 p-4 rounded-xl border border-gray-200 bg-white shadow-sm"
                >
                  <div className="flex justify-between items-center mb-2">
                    <Label
                      htmlFor={`kpi-${kpiId}`}
                      className="text-sm font-medium text-gray-700"
                    >
                      {kpiTitle}
                    </Label>
                    <span className="text-xs text-gray-500">{`Max Score: ${kpiWeight}`}</span>
                  </div>

                  <TextInput
                    id={`kpi-${kpiId}`}
                    type="number"
                    step="0.01"
                    Max
                    Score={kpiWeight}
                    min={0}
                    value={formData[kpiId] ?? ""}
                    onChange={(e) => handleChange(kpiId, e.target.value)}
                    className="w-full"
                  />

                  <p className="mt-2 text-sm text-gray-600 italic">
                    Staff Self-Rating:{" "}
                    <span className="font-semibold text-gray-800">
                      {kpiScore}
                    </span>
                  </p>
                </div>
              );
            })}

            <div className="flex gap-4 mt-4">
              {!isReviewMode && (
                <Button
                  onClick={() => handleScoreUpdate(editingAppraisal.id, true)}
                >
                  Update Self Score
                </Button>
              )}

              {isReviewMode && isAdmin && (
                <Button
                  color="success"
                  onClick={() => handleScoreUpdate(editingAppraisal.id)}
                >
                  Update Actual Performance
                </Button>
              )}
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={!!viewingAppraisal}
          onClose={() => setViewingAppraisal(null)}
          size="xl"
          className="z-[10000]"
        >
          <Modal.Header>
            Appraisal Details ({viewingAppraisal?.staffId?.firstName}{" "}
            {viewingAppraisal?.staffId?.lastName} {"for "}{" "}
            {viewingAppraisal?.month} {viewingAppraisal?.year})
          </Modal.Header>
          <Modal.Body>
            {viewingAppraisal?.kpis?.length > 0 ? (
              <ul className="space-y-4">
                {viewingAppraisal.kpis.map((b, index) => (
                  <li
                    key={b.kpiId?.id || index}
                    className="bg-gray-100 rounded-lg p-4 border shadow-sm"
                  >
                    <p className="font-medium text-gray-800 text-base mb-2">
                      {b.kpiId?.title}
                    </p>
                    <div className="text-sm text-gray-700">
                      <p>
                        <strong>Self Score:</strong> {b.score} /{" "}
                        {b.kpiId?.weight}
                      </p>
                      {b.actualPerformance != null && (
                        <p>
                          <strong>Actual Performance:</strong>{" "}
                          {b.actualPerformance}
                        </p>
                      )}
                      {b.remarks && (
                        <p>
                          <strong>Remarks:</strong> {b.remarks}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">No KPI data available.</p>
            )}
          </Modal.Body>
        </Modal>
      </DashboardLayout>

      {toast.open && (
        <CustomToast
          message={toast.msg}
          type={toast.type}
          duration={5000}
          onClose={() => setToast({ open: false, msg: "", type: "" })}
        />
      )}
    </>
  );
}

export default Appraisal;
